import { Monitor, Smartphone, X } from "lucide-react";
import { Button } from "@/components/ui/button";
import { EmptyState } from "@/components/v2/EmptyState";
import { useToast } from "@/components/v2/Toast";
import {
  useRevokeOtherSessions,
  useRevokeSession,
  useSessions,
  type Session,
} from "@/lib/auth-me";
import { History } from "lucide-react";

/**
 * Active-sessions table. Each row shows parsed user-agent + IP +
 * issued/expiry timestamps. Operator can revoke individual rows or
 * "Sign out everywhere" — both bounce the affected sessions on next
 * API call.
 */
export function SessionsTable() {
  const q = useSessions();
  const revoke = useRevokeSession();
  const revokeOthers = useRevokeOtherSessions();
  const toast = useToast();

  if (q.isLoading) {
    return <p className="text-sm text-muted-foreground py-4">Loading sessions…</p>;
  }
  if (q.isError) {
    return (
      <p className="text-sm text-destructive py-4">
        Couldn't load sessions: {(q.error as Error).message}
      </p>
    );
  }

  const sessions = q.data?.sessions ?? [];

  if (sessions.length === 0) {
    return (
      <EmptyState
        bare
        icon={History}
        title="No active sessions"
        description="You're not signed in anywhere right now (which is unusual since you're seeing this)."
      />
    );
  }

  const others = sessions.filter((s) => !s.current);

  return (
    <div className="space-y-3">
      <ul className="rounded-md border bg-card divide-y">
        {sessions.map((s) => (
          <SessionRow
            key={s.id}
            session={s}
            onRevoke={() =>
              revoke.mutate(s.id, {
                onSuccess: () =>
                  toast.success(s.current ? "Signed out" : "Session revoked"),
                onError: (e) =>
                  toast.error("Couldn't revoke", (e as Error).message),
              })
            }
            pending={revoke.isPending}
          />
        ))}
      </ul>
      {others.length > 0 && (
        <div className="flex justify-end">
          <Button
            size="sm"
            variant="outline"
            onClick={() =>
              revokeOthers.mutate(undefined, {
                onSuccess: () =>
                  toast.success("Other sessions signed out"),
                onError: (e) =>
                  toast.error("Couldn't revoke", (e as Error).message),
              })
            }
            disabled={revokeOthers.isPending}
          >
            {revokeOthers.isPending ? "Signing out…" : "Sign out everywhere else"}
          </Button>
        </div>
      )}
    </div>
  );
}

function SessionRow({
  session,
  onRevoke,
  pending,
}: {
  session: Session;
  onRevoke: () => void;
  pending: boolean;
}) {
  const { device, browser } = parseUserAgent(session.userAgent ?? "");
  const Icon = device === "mobile" ? Smartphone : Monitor;

  return (
    <li className="px-4 py-3 flex items-center gap-3">
      <span className="inline-flex h-9 w-9 items-center justify-center rounded-full bg-muted text-foreground/70">
        <Icon className="h-4 w-4" />
      </span>
      <div className="flex-1 min-w-0">
        <p className="text-sm font-medium">
          {browser}
          {session.current && (
            <span className="ml-2 inline-flex items-center rounded-full bg-status-completed/10 text-status-completed px-2 py-0.5 text-[11px] font-medium">
              This device
            </span>
          )}
        </p>
        <p className="text-xs text-muted-foreground mt-0.5">
          {session.ip ?? "Unknown IP"} · Issued{" "}
          {new Date(session.issuedAt).toLocaleString()}
        </p>
      </div>
      <Button
        size="sm"
        variant="ghost"
        className="text-muted-foreground hover:text-destructive"
        onClick={onRevoke}
        disabled={pending}
        aria-label="Revoke session"
      >
        <X className="h-4 w-4" />
      </Button>
    </li>
  );
}

/**
 * Lightweight UA parser. Avoids pulling `ua-parser-js` for two strings —
 * we just want a recognizable label.
 */
function parseUserAgent(ua: string): { device: "desktop" | "mobile"; browser: string } {
  if (!ua) return { device: "desktop", browser: "Unknown browser" };
  const isMobile = /Android|iPhone|iPad|iPod|Mobile/.test(ua);
  let browser = "Unknown browser";
  if (/Edg\//.test(ua)) browser = "Microsoft Edge";
  else if (/OPR\//.test(ua)) browser = "Opera";
  else if (/Chrome\//.test(ua)) browser = "Chrome";
  else if (/Firefox\//.test(ua)) browser = "Firefox";
  else if (/Safari\//.test(ua)) browser = "Safari";

  let os = "";
  if (/Mac OS X/.test(ua)) os = "macOS";
  else if (/Windows/.test(ua)) os = "Windows";
  else if (/Linux/.test(ua)) os = "Linux";
  else if (/Android/.test(ua)) os = "Android";
  else if (/iPhone|iPad|iPod/.test(ua)) os = "iOS";

  return {
    device: isMobile ? "mobile" : "desktop",
    browser: os ? `${browser} on ${os}` : browser,
  };
}
